import React, {ComponentType} from 'react';

import {
  DocumentPrivateStatusButton,
  DocumentSequencedStepsStatusButton,
  DocumentSubscribedStatusButton,
} from 'app/general/doc-status';

import type {
  ContentType,
  DetailPolicySerializedType,
  KnowledgebaseSerializedType,
  ProcedureSerializedType,
  ProcessSerializedType,
  QuizSerializedType,
  TaskInstanceSerializedType,
} from 'sweet-types';

type TaskAssignedDocument =
  TaskInstanceSerializedType['template']['assigned_object'];

interface ButtonComponentProp<T, CT> {
  document: T;
  canEdit: boolean;
  contentType: CT;
}
type ContextType = 'task' | 'view' | 'edit';

const AVAILABLE_STATUSES: Array<
  Array<{
    name: string;
    cts: Array<ContentType>;
    whitelistContext: Array<ContextType>;
    Component: ComponentType<ButtonComponentProp<any, any>>;
  }>
> = [
  [
    {
      name: 'subscribed',
      cts: ['procedure', 'policy', 'proceduregroup', 'knowledgebase'],
      whitelistContext: ['view', 'edit'],
      Component: DocumentSubscribedStatusButton,
    },
  ],
  [
    {
      name: 'sequenced',
      whitelistContext: ['view', 'edit', 'task'],
      cts: ['procedure', 'proceduregroup'],
      Component: function SequencedIconStatus({
        document,
        canEdit,
        contentType,
      }: ButtonComponentProp<
        ProcedureSerializedType | ProcessSerializedType | TaskAssignedDocument,
        'procedure' | 'proceduregroup'
      >) {
        return (
          <DocumentSequencedStepsStatusButton
            contentType={contentType}
            // task-assigned document has no URL, so the button will be read-only
            documentURL={'url' in document ? document.url : ''}
            canEdit={'url' in document ? canEdit : false}
            isSequenced={document.steps_blocked_at != null}
          />
        );
      },
    },
    {
      name: 'private',
      whitelistContext: ['view', 'edit'],
      cts: ['procedure', 'policy'],
      Component: function SequencedIconStatus({
        document,
        contentType,
        canEdit,
      }: ButtonComponentProp<
        ProcedureSerializedType | DetailPolicySerializedType,
        'procedure' | 'policy'
      >) {
        return contentType === 'procedure' || contentType === 'policy' ? (
          <DocumentPrivateStatusButton
            object={document}
            ct={contentType}
            canEdit={canEdit}
            isPrivate={document.private}
          />
        ) : null;
      },
    },
  ],
];

interface Props {
  canEdit: boolean;
  contentType: ContentType;
  document:
    | ProcedureSerializedType
    | DetailPolicySerializedType
    | ProcessSerializedType
    | QuizSerializedType
    | KnowledgebaseSerializedType
    | TaskAssignedDocument;
  context: ContextType;
}
export const DocumentMetadataStatusButtons = ({
  document,
  contentType,
  context,
  canEdit,
}: Props): JSX.Element => {
  return (
    <>
      {AVAILABLE_STATUSES.map((buttons, i) => (
        <div key={i} className="btn-group ml-2 d-print-none" role="group">
          {buttons.map(({name, cts, whitelistContext, Component}) =>
            cts.indexOf(contentType) > -1 &&
            whitelistContext.indexOf(context) > -1 ? (
              <Component
                canEdit={canEdit}
                key={name}
                document={document}
                contentType={contentType}
              />
            ) : null
          )}
        </div>
      ))}
    </>
  );
};
